import React, { useState, useRef, useCallback } from 'react';
import { Download } from 'lucide-react';
import { restClient } from '../RestClient';
import { LoadingButton } from '../LoadingButton';
import { CsvUploadResult } from '../Model';

interface PlayerUploadProps {
  selectionDataId: number;
  onUploadComplete: (result: CsvUploadResult) => void;
  onError: (message: string) => void;
}

export function PlayerUpload({ selectionDataId, onUploadComplete, onError }: PlayerUploadProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<CsvUploadResult | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const result = await restClient.downloadCsvTemplate(selectionDataId);
      if (result.IsSuccess) {
        const url = window.URL.createObjectURL(result.Value);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'players_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        onError(`${result.Error.code}: ${result.Error.description}`);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith('.csv')) {
      setSelectedFile(file);
      setUploadResult(null);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleDropzoneClick = () => {
    fileInputRef.current?.click();
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const result = await restClient.uploadPlayersCsv(selectedFile, selectionDataId);
      if (result.IsSuccess) {
        setUploadResult(result.Value);
        onUploadComplete(result.Value);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        onError(`${result.Error.code}: ${result.Error.description}`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="player-upload-panel">
      <h3 className="player-upload-title">
        Upload Players from CSV
      </h3>

      {/* Download Template */}
      <div className="player-upload-template">
        <LoadingButton
          isLoading={isDownloading}
          onClick={handleDownloadTemplate}
          className="btn btn-secondary btn-sm"
          loadingText="Downloading..."
        >
          <Download size={14} />
          Download CSV Template
        </LoadingButton>
      </div>

      {/* Dropzone */}
      <div
        className={`player-upload-dropzone ${isDragActive ? 'player-upload-dropzone-active' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleDropzoneClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        <div className="player-upload-dropzone-text">
          Drag & drop CSV here
        </div>
        <div className="player-upload-dropzone-or">or</div>
        <button type="button" className="btn btn-secondary btn-sm">
          Browse files
        </button>
      </div>

      {/* Selected file */}
      {selectedFile && (
        <div className="player-upload-file-selected">
          <span className="player-upload-file-name">{selectedFile.name}</span>
          <button
            className="player-upload-file-clear"
            onClick={handleClearFile}
            title="Remove file"
          >
            ×
          </button>
        </div>
      )}

      {/* Upload action */}
      <div className="player-upload-actions">
        <LoadingButton
          isLoading={isUploading}
          onClick={handleUpload}
          disabled={!selectedFile}
          className={`btn ${selectedFile ? 'btn-primary' : 'btn-secondary'}`}
          loadingText="Uploading..."
        >
          Upload CSV
        </LoadingButton>
      </div>

      <p className="player-upload-hint">
        We'll validate the file before importing
      </p>

      {uploadResult && (
        <div className="player-upload-result">
          <div className="font-semibold mb-1">Upload Complete</div>
          <div>Players added: {uploadResult.playersAdded}</div>
          <div>Players updated: {uploadResult.playersUpdated}</div>
        </div>
      )}
    </div>
  );
}
